import React from "react";
import useSearchState from "../../../useSearchState";
import { buildYearOptions } from "../../../utils";
import useYears from "../date/useYears";
import { createDefaultInput } from "./inputs";
import { minMaxRangeCheck, validateInput } from "./validation";
import Immutable from "immutable";
import { datePickerYearsType, rangeCreateControlType } from "../../../../common/types/search/search";

export interface RangeFieldsPassedProps {
	createControl?: rangeCreateControlType;
	filterDate?: () => void;
	inputMax?: string;
	inputMin?: string;
	placeholder?: string;
	rangeCheck?: () => void;
	yearMax?: number;
	yearMin?: number;
	yearMinKey?: string;
	yearMinKe?: string;
}

export interface RangeFieldsProps extends RangeFieldsPassedProps {
	criterionKey: string;
	datesRange: boolean;
	guideline?: string;
	label: string;
	onCriterionInputChange: (input: Immutable.Map<string, string>) => void;
	onInputChange: (fieldKey: string, value: string) => void;
	yearsRange?: boolean;
}

export function RangeFields({
	createControl = createDefaultInput,
	criterionKey,
	datesRange,
	filterDate,
	guideline,
	inputMin,
	inputMax,
	label,
	onCriterionInputChange,
	onInputChange,
	placeholder = "0",
	rangeCheck = minMaxRangeCheck,
	yearMax = new Date().getFullYear(),
	yearMin,
	yearMinKey,
	yearsRange,
}: RangeFieldsProps) {
	const { currentInput } = useSearchState();
	const { yearMinData } = useYears(yearMinKey || criterionKey);
	const inputMinId = criterionKey + "_from";
	const inputMaxId = criterionKey + "_to";
	const inputMinLabel = datesRange ? "Date from" : "From";
	const inputMaxLabel = datesRange ? "Date to" : "To";

	const filterYears = (
		unfilteredYears: datePickerYearsType,
		designator: "from" | "to",
	) => {
		if (!yearsRange) {
			return unfilteredYears;
		}
		if (designator === "from") {
			const to = parseInt(currentInput.getIn([criterionKey, "max"]), 10);
			if (!to) {
				return unfilteredYears;
			}
			return unfilteredYears.filter(({ key }) => key <= to);
		}
		if (designator === "to") {
			const from = parseInt(
				currentInput.getIn([criterionKey, "min"]),
				10,
			);
			if (!from) {
				return unfilteredYears;
			}
			return unfilteredYears.filter(({ key }) => key >= from);
		}
		return unfilteredYears;
	};

	let years: datePickerYearsType = [];

	if (!yearMin) {
		if (yearMinData) {
			years = buildYearOptions(yearMinData, yearMax).reverse();
		}
	} else {
		years = buildYearOptions(yearMin, yearMax).reverse();
	}

	const validationResult = validateInput(
		rangeCheck,
		inputMin,
		inputMax,
		currentInput,
	);

	const error = validationResult.isError() && (
		<span className="range__error">{validationResult.getMessage()}</span>
	);

	return (
		<div className="range__fields-wrapper flex">
			<div className="range__fields flex">
				<div className="range-field">
					<label htmlFor={inputMinId}>{inputMinLabel}</label>
					{createControl(
						inputMinId,
						label + " from",
						(value: string) => onInputChange("min", value),
						onCriterionInputChange,
						placeholder,
						inputMin,
						filterDate,
						filterYears(years, "from"),
						criterionKey,
					)}
				</div>

				<span className="range-to hide-mobile">to</span>

				<div className="range-field">
					<label htmlFor={inputMaxId}>{inputMaxLabel}</label>
					{createControl(
						inputMaxId,
						label + " to",
						(value: string) => onInputChange("max", value),
						onCriterionInputChange,
						placeholder,
						inputMax,
						filterDate,
						filterYears(years, "to"),
						criterionKey,
					)}
				</div>
			</div>

			{!datesRange && guideline && (
				<span className="range__guideline">{guideline}</span>
			)}
			{error}
		</div>
	);
}
